'use client'

import { useState, useRef, useEffect } from 'react'
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/react'
import {
  Bars3Icon,
  ChartPieIcon,
  CursorArrowRaysIcon,
  FingerPrintIcon,
  SquaresPlusIcon,
  XMarkIcon,
  CommandLineIcon,
  DocumentTextIcon,
  BookOpenIcon,
  CodeBracketIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  SignalIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  BeakerIcon,
  RocketLaunchIcon,
  BuildingLibraryIcon,
  GlobeAltIcon,
  WrenchScrewdriverIcon,
  PresentationChartLineIcon,
  MagnifyingGlassIcon,
  ComputerDesktopIcon,
  ShareIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { SignedIn, SignedOut } from '@clerk/clerk-react'
import { Link } from '@tanstack/react-router'
import { cn } from '../../lib/utils'
import PromptInput from '../PromptInput'

const buttonBaseClasses = cn(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  "disabled:pointer-events-none disabled:opacity-50",
  "h-9 w-18 sm:w-22"
)

const platform = [
  { name: 'Visual Editor', description: 'Build beautiful data apps with our visual editor', href: '/platform/visual-editor', icon: ChartPieIcon },
  { name: 'Agent', description: 'Let AI help you build your data apps', href: '/platform/agent', icon: CursorArrowRaysIcon },
  { name: 'Projects Dashboard', description: 'Manage all your projects in one place', href: '/platform/projects-dashboard', icon: FingerPrintIcon },
  { name: 'Live Previews', description: 'Preview your changes in real-time', href: '/platform/live-previews', icon: SquaresPlusIcon },
  { name: 'Team Collaboration', description: 'Work together with your team', href: '/platform/team-collaboration', icon: UserGroupIcon },
  { name: 'Export & Share', description: 'Share your work with others', href: '/platform/export-and-share', icon: ShareIcon },
]

const solutions = {
  byRole: [
    { name: 'For Engineers', href: '/solutions/for-engineers', icon: CommandLineIcon },
    { name: 'For Ops & Analysts', href: '/solutions/for-ops-and-analysts', icon: ChartPieIcon },
    { name: 'For Students', href: '/solutions/for-students', icon: AcademicCapIcon },
    { name: 'For Researchers', href: '/solutions/for-researchers', icon: BeakerIcon },
  ],
  byOrganization: [
    { name: 'Enterprises', href: '/solutions/for-enterprises', icon: GlobeAltIcon },
    { name: 'Governments', href: '/solutions/for-governments', icon: BuildingLibraryIcon },
  ],
  byUseCase: [
    { name: 'For Internal Tools', href: '/solutions/for-internal-tools', icon: WrenchScrewdriverIcon },
    { name: 'For Interactive Reports', href: '/solutions/for-interactive-reports', icon: PresentationChartLineIcon },
    { name: 'For Exploratory Analysis', href: '/solutions/for-exploratory-analysis', icon: MagnifyingGlassIcon },
    { name: 'For Client-Facing Dashboards', href: '/solutions/for-client-facing-dashboards', icon: ComputerDesktopIcon },
  ],
}

const developers = [
  { name: 'Quickstart Guide', href: 'https://docs.preswald.com/quickstart', icon: RocketLaunchIcon },
  { name: 'Documentation', href: 'https://docs.preswald.com', icon: BookOpenIcon },
  { name: 'GitHub', href: 'https://github.com/StructuredLabs/preswald', icon: CodeBracketIcon },
  { name: 'Changelog', href: 'https://github.com/StructuredLabs/preswald/releases', icon: DocumentTextIcon },
  { name: 'Issues', href: 'https://github.com/StructuredLabs/preswald/issues', icon: SignalIcon },
  { name: 'Contributing', href: 'https://github.com/StructuredLabs/preswald/blob/main/CONTRIBUTING.md', icon: BriefcaseIcon },
  { name: 'Community', href: 'https://join.slack.com/t/structuredlabs-users/shared_invite/zt-33zwhyv3l-6Xu4bHL6b6~bI3z9fvlUig', icon: ChatBubbleLeftRightIcon },
]

const resources = [
  { name: 'Blog', href: '/blog', icon: DocumentTextIcon },
  { name: 'Careers', href: 'https://www.ycombinator.com/companies/structured-labs/jobs', icon: BriefcaseIcon },
  { name: 'Contact', href: 'mailto:contact@structuredlabs.com', icon: ChatBubbleLeftRightIcon },
  { name: 'Book a Demo', href: 'https://cal.com/amruthagujjar/30min', icon: CalendarIcon },
  { name: 'Hackathon', href: '/hackathon', icon: RocketLaunchIcon, target: '_blank', rel: 'noopener noreferrer' },
  { name: 'Status', href: 'https://preswald.instatus.com/', icon: SignalIcon },
]

function useHoverPopover() {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout>()

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsOpen(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, 100) // Small delay to prevent menu from closing when moving between button and panel
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    isOpen,
    setIsOpen,
    buttonProps: {
      ref: buttonRef,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    },
    panelProps: {
      ref: panelRef,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    },
  }
}

export default function LandingHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const platformPopover = useHoverPopover()
  const solutionsPopover = useHoverPopover()
  const developersPopover = useHoverPopover()
  const resourcesPopover = useHoverPopover()
  const [selectedModel, setSelectedModel] = useState('')
  const [modelOptions, setModelOptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentPlaceholder, setCurrentPlaceholder] = useState('')
  const [ellipsis, setEllipsis] = useState('...')
  const [isSliding, setIsSliding] = useState(false)

  const handleSubmit = (value) => {
    // Handle form submission
  }

  const handleStopGeneration = () => {
    // Handle stopping generation
  }

  return (
    <header className="font-sans border border-[#e0e0e0] bg-background z-50 relative">
      <nav aria-label="Global" className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
        <div className="flex lg:flex-1">
          <Link to="/home" className="-m-1.5 p-1.5">
            <span className="sr-only">Preswald</span>
          <img
            src="/logo.png"
            alt="Preswald Logo"
              className="h-8 w-auto"
          />
        </Link>
      </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
        </div>
        <PopoverGroup className="hidden lg:flex lg:gap-x-6">
          <Popover className="relative">
            <PopoverButton 
              {...platformPopover.buttonProps}
              className={cn(
                "flex items-center gap-x-1 text-base/6 font-medium tracking-wide text-gray-900",
                "rounded-full px-4 py-2 hover:bg-white hover:shadow-sm",
                "transition-all"
              )}
            >
              Platform
              <ChevronDownIcon 
                aria-hidden="true" 
                className={cn(
                  "size-5 flex-none text-gray-400 transition-transform duration-200",
                  platformPopover.isOpen && "rotate-180"
                )} 
              />
            </PopoverButton>

            <PopoverPanel
              {...platformPopover.panelProps}
              static
              className={cn(
                "absolute -left-8 top-full z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition duration-200",
                platformPopover.isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none"
              )}
            >
              <div className="p-4">
                {platform.map((item) => (
                  <div
                    key={item.name}
                    className="group relative flex gap-x-6 rounded-lg p-4 text-sm/6 hover:bg-gray-50"
                  >
                    <div className="mt-1 flex size-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                      <item.icon aria-hidden="true" className="size-6 text-gray-600 group-hover:text-slate-600" />
                    </div>
                    <div className="flex-auto">
                      <a href={item.href} className="block font-semibold text-gray-900" {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                        {item.name}
                        <span className="absolute inset-0" />
                      </a>
                      <p className="mt-1 text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>

          <Popover className="relative">
            <PopoverButton 
              {...solutionsPopover.buttonProps}
              className={cn(
                "flex items-center gap-x-1 text-base/6 font-medium tracking-wide text-gray-900",
                "rounded-full px-4 py-2 hover:bg-white hover:shadow-sm",
                "transition-all"
              )}
            >
              Solutions
              <ChevronDownIcon 
                aria-hidden="true" 
                className={cn(
                  "size-5 flex-none text-gray-400 transition-transform duration-200",
                  solutionsPopover.isOpen && "rotate-180"
                )} 
              />
            </PopoverButton>

            <PopoverPanel
              {...solutionsPopover.panelProps}
              static
              className={cn(
                "absolute -left-8 top-full z-10 mt-3 w-screen max-w-3xl overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition duration-200",
                solutionsPopover.isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none"
              )}
            >
              <div className="p-4">
                <div className="grid grid-cols-3 gap-8">
                  <div>
                    <h3 className="text-sm/6 font-semibold tracking-tight text-gray-900 mb-4">By Role</h3>
                    <div className="space-y-2">
                      {solutions.byRole.map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          className="group relative flex items-center gap-x-3 rounded-lg p-2 text-sm/6 hover:bg-gray-50"
                          {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          <div className="flex size-8 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                            <item.icon aria-hidden="true" className="size-5 text-gray-600 group-hover:text-slate-600" />
                          </div>
                          <span className="text-gray-900 font-medium">{item.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm/6 font-semibold tracking-tight text-gray-900 mb-4">By Use-Case</h3>
                    <div className="space-y-2">
                      {solutions.byUseCase.map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          className="group relative flex items-center gap-x-3 rounded-lg p-2 text-sm/6 hover:bg-gray-50"
                          {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          <div className="flex size-8 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                            <item.icon aria-hidden="true" className="size-5 text-gray-600 group-hover:text-slate-600" />
                          </div>
                          <span className="text-gray-900 font-medium">{item.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="space-y-3">
                      {solutions.byOrganization.map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          className="group relative flex flex-col gap-y-2 rounded-lg p-4 text-sm/6 border border-gray-200 overflow-hidden
                            transition-all duration-300 ease-out
                            hover:scale-[1.02] hover:shadow-lg hover:border-gray-300
                            hover:bg-gradient-to-br hover:from-white hover:to-gray-50"
                          {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white -z-10 transition-opacity group-hover:opacity-0" />
                          <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-[0.07] transition-transform duration-300 group-hover:scale-110 group-hover:translate-x-2">
                            <item.icon className="size-24 text-gray-900" aria-hidden="true" />
                          </div>
                          <span className="text-gray-900 font-semibold">{item.name}</span>
                          <p className="text-sm text-gray-600 max-w-[80%]">
                            {item.name === 'Enterprises' 
                              ? 'Build and deploy internal tools for your organization'
                              : 'Secure and compliant solutions for government agencies'}
                          </p>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </PopoverPanel>
          </Popover>

          <Popover className="relative">
            <PopoverButton 
              {...developersPopover.buttonProps}
              className={cn(
                "flex items-center gap-x-1 text-base/6 font-medium tracking-wide text-gray-900",
                "rounded-full px-4 py-2 hover:bg-white hover:shadow-sm",
                "transition-all"
              )}
            >
              Developers
              <ChevronDownIcon 
                aria-hidden="true" 
                className={cn(
                  "size-5 flex-none text-gray-400 transition-transform duration-200",
                  developersPopover.isOpen && "rotate-180"
                )} 
              />
            </PopoverButton>

            <PopoverPanel
              {...developersPopover.panelProps}
              static
              className={cn(
                "absolute -left-8 top-full z-10 mt-3 w-56 rounded-xl bg-white p-2 shadow-lg ring-1 ring-gray-900/5 transition duration-200",
                developersPopover.isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none"
              )}
            >
              {developers.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="group relative flex items-center gap-x-3 rounded-lg p-2 text-sm/6 hover:bg-gray-50"
                  {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  <div className="flex size-8 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                    <item.icon aria-hidden="true" className="size-5 text-gray-600 group-hover:text-slate-600" />
                  </div>
                  <span className="text-gray-900 font-medium">{item.name}</span>
                </a>
              ))}
            </PopoverPanel>
          </Popover>

          <Popover className="relative">
            <PopoverButton 
              {...resourcesPopover.buttonProps}
              className={cn(
                "flex items-center gap-x-1 text-base/6 font-medium tracking-wide text-gray-900",
                "rounded-full px-4 py-2 hover:bg-white hover:shadow-sm",
                "transition-all"
              )}
            >
              Resources
              <ChevronDownIcon 
                aria-hidden="true" 
                className={cn(
                  "size-5 flex-none text-gray-400 transition-transform duration-200",
                  resourcesPopover.isOpen && "rotate-180"
                )} 
              />
            </PopoverButton>

            <PopoverPanel
              {...resourcesPopover.panelProps}
              static
              className={cn(
                "absolute -left-8 top-full z-10 mt-3 w-56 rounded-xl bg-white p-2 shadow-lg ring-1 ring-gray-900/5 transition duration-200",
                resourcesPopover.isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none"
              )}
            >
              {resources.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="group relative flex items-center gap-x-3 rounded-lg p-2 text-sm/6 hover:bg-gray-50"
                  {...(item.href.startsWith('http') || item.target ? { target: item.target || '_blank', rel: item.rel || 'noopener noreferrer' } : {})}
                >
                  <div className="flex size-8 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                    <item.icon aria-hidden="true" className="size-5 text-gray-600 group-hover:text-slate-600" />
                  </div>
                  <span className="text-gray-900 font-medium">{item.name}</span>
                </a>
              ))}
            </PopoverPanel>
          </Popover>

          <Link 
            to="/pricing" 
            className={cn(
              "text-base/6 font-medium tracking-wide text-gray-900",
              "rounded-full px-4 py-2 hover:bg-white hover:shadow-sm",
              "transition-all"
            )}
          >
            Pricing
          </Link>
        </PopoverGroup>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-4">
        {SignedIn({
          children: (
            <Link
              to="/dashboard"
              className={cn(
                buttonBaseClasses,
                "bg-primary text-primary-foreground hover:bg-primary/90"
              )}
            >
              Dashboard
            </Link>
          )
        })}
        {SignedOut({
          children: (
              <>
              <Link
                to="/sign-in"
                className={cn(
                  buttonBaseClasses,
                  "border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                )}
              >
                  Sign in
              </Link>
              <Link
                to="/sign-up"
                className={cn(
                  buttonBaseClasses,
                  "bg-primary text-primary-foreground hover:bg-primary/90"
                )}
              >
                  Sign up
                </Link>
              </>
            )
          })}
        </div>
      </nav>
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-10" />
        <DialogPanel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <Link to="/home" className="-m-1.5 p-1.5">
              <span className="sr-only">Preswald</span>
              <img
                src="/logo.png"
                alt="Preswald Logo"
                className="h-8 w-auto"
              />
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                <Disclosure as="div" className="-mx-3">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50">
                    Platform
                    <ChevronDownIcon aria-hidden="true" className="size-5 flex-none group-data-[open]:rotate-180" />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    {platform.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as="a"
                        href={item.href}
                        className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-gray-900 hover:bg-gray-50"
                        {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>

                <Disclosure as="div" className="-mx-3">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50">
                    Solutions
                    <ChevronDownIcon aria-hidden="true" className="size-5 flex-none group-data-[open]:rotate-180" />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    <div className="pl-3">
                      <h4 className="text-sm/7 font-semibold text-gray-900 mb-2">By Role</h4>
                      <div className="space-y-1">
                        {solutions.byRole.map((item) => (
                          <DisclosureButton
                            key={item.name}
                            as="a"
                            href={item.href}
                            className="block rounded-lg py-2 pl-3 pr-3 text-sm/7 text-gray-900 hover:bg-gray-50"
                            {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          >
                            {item.name}
                          </DisclosureButton>
                        ))}
                      </div>
                    </div>
                    <div className="pl-3">
                      <h4 className="text-sm/7 font-semibold text-gray-900 mb-2">By Use-Case</h4>
                      <div className="space-y-1">
                        {solutions.byUseCase.map((item) => (
                          <DisclosureButton
                            key={item.name}
                            as="a"
                            href={item.href}
                            className="block rounded-lg py-2 pl-3 pr-3 text-sm/7 text-gray-900 hover:bg-gray-50"
                            {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          >
                            {item.name}
                          </DisclosureButton>
                        ))}
                      </div>
                    </div>
                    <div className="pl-3">
                      <h4 className="text-sm/7 font-semibold text-gray-900 mb-2">By Organization</h4>
                      <div className="space-y-3">
                        {solutions.byOrganization.map((item) => (
                          <DisclosureButton
                            key={item.name}
                            as="a"
                            href={item.href}
                            className="group block rounded-lg p-3 text-sm/7 text-gray-900 border border-gray-200 overflow-hidden relative
                              transition-all duration-300 ease-out
                              hover:scale-[1.02] hover:shadow-lg hover:border-gray-300
                              hover:bg-gradient-to-br hover:from-white hover:to-gray-50"
                            {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          >
                            <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white -z-10 transition-opacity group-hover:opacity-0" />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-[0.07] transition-transform duration-300 group-hover:scale-110 group-hover:translate-x-2">
                              <item.icon className="size-20 text-gray-900" aria-hidden="true" />
                            </div>
                            <span className="font-semibold mb-1 block">{item.name}</span>
                            <p className="text-sm text-gray-600 max-w-[80%]">
                              {item.name === 'Enterprises' 
                                ? 'Build and deploy internal tools for your organization'
                                : 'Secure and compliant solutions for government agencies'}
                            </p>
                          </DisclosureButton>
                        ))}
                      </div>
                    </div>
                  </DisclosurePanel>
                </Disclosure>

                <Disclosure as="div" className="-mx-3">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50">
                    Developers
                    <ChevronDownIcon aria-hidden="true" className="size-5 flex-none group-data-[open]:rotate-180" />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    {developers.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as="a"
                        href={item.href}
                        className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-gray-900 hover:bg-gray-50"
                        {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>

                <Link
                  to="/pricing"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                >
                  Pricing
              </Link>

                <Disclosure as="div" className="-mx-3">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50">
                    Resources
                    <ChevronDownIcon aria-hidden="true" className="size-5 flex-none group-data-[open]:rotate-180" />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    {resources.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as="a"
                        href={item.href}
                        className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-gray-900 hover:bg-gray-50"
                        {...(item.href.startsWith('http') || item.target ? { target: item.target || '_blank', rel: item.rel || 'noopener noreferrer' } : {})}
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>
            </div>
              <div className="py-6">
                {SignedIn({
                  children: (
                    <Link
                      to="/dashboard"
                      className={cn(
                        buttonBaseClasses,
                        "bg-primary text-primary-foreground hover:bg-primary/90 w-full"
                      )}
                    >
                      Dashboard
                    </Link>
                  )
                })}
                {SignedOut({
                  children: (
                    <div className="flex gap-x-2">
                      <Link
                        to="/sign-in"
                        className={cn(
                          buttonBaseClasses,
                          "border border-input bg-background hover:bg-accent hover:text-accent-foreground w-full"
                        )}
                      >
                        Sign in
                      </Link>
                      <Link
                        to="/sign-up"
                        className={cn(
                          buttonBaseClasses,
                          "bg-primary text-primary-foreground hover:bg-primary/90 w-full"
                        )}
                      >
                        Sign up
                      </Link>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  )
} 